import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Me } from "@workcore/schemas";
import { get } from "../api";
import { Breadcrumbs, ErrorBox, PageHeader, StatusBadge } from "../ui";
import { InlineText, WriteError, useCanEdit, useDefinition, useDefinitionWrite, useUsage } from "./editor/shared";
import { StructureTab } from "./editor/Structure";
import { StatesTab } from "./editor/States";
import { FieldsTab } from "./editor/Fields";
import { HistoryTab } from "./editor/History";
import { TimeboxesTab } from "./editor/Timeboxes";
import { downloadExport } from "./Workflows";

type Tab = "structure" | "states" | "fields" | "timeboxes" | "history";
const TABS: { id: Tab; label: string }[] = [
  { id: "structure", label: "Types & structure" },
  { id: "states", label: "States & transitions" },
  { id: "fields", label: "Fields" },
  { id: "timeboxes", label: "Timeboxes" },
  { id: "history", label: "History" },
];

export function WorkflowEditorPage() {
  const { slug = "", id: idParam = "" } = useParams();
  const id = Number(idParam);
  const def = useDefinition(slug, id);
  const usage = useUsage(slug, id);
  const canEdit = useCanEdit(slug, id);
  const me = useQuery({ queryKey: ["ws-me", slug], queryFn: async () => Me.parse(await get(`/v1/ws/${slug}/me`)) });
  const write = useDefinitionWrite(slug, id);
  const [tab, setTab] = useState<Tab>("structure");
  const d = def.data;
  return (
    <>
      <ErrorBox error={def.error} />
      {d && (
        <>
          <PageHeader
            back={<Breadcrumbs items={[{ label: "Workflows", to: `/w/${slug}/workflows` }, { label: d.name }]} />}
            title={
              <>
                <InlineText label="workflow name" value={d.name} disabled={!canEdit} onSave={(v) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/definitions/${id}`, body: { name: v } })} />
                <StatusBadge status={d.status} />
              </>
            }
            description={
              <>
                <code>{d.key}</code> · {d.types.length} types · {d.fields.filter((f) => f.status === "active").length} fields
                {!canEdit && " · read only (you need workflow admin on this workflow)"}
              </>
            }
            actions={
          <>
            <button className="secondary" onClick={() => void downloadExport(slug, id, d.key)}>Export JSON</button>
            {me.data?.isSystemAdmin && (
              <button
                className="secondary"
                onClick={() =>
                  write.mutate({
                    method: "PATCH",
                    path: `/v1/ws/${slug}/definitions/${id}`,
                    body: { status: d.status === "active" ? "archived" : "active" },
                    ...(d.status === "active" ? { impact: `Archive '${d.name}'. No new projects can use it; existing ones carry on.` } : {}),
                  })
                }
              >
                {d.status === "active" ? "Archive" : "Unarchive"}
              </button>
            )}
          </>
            }
          />
          <WriteError error={write.error} />
          <nav className="tabs">
            {TABS.map((t) => (
              <button key={t.id} className={tab === t.id ? "active" : ""} onClick={() => setTab(t.id)}>
                {t.label}
              </button>
            ))}
          </nav>
          {tab === "structure" && <StructureTab slug={slug} def={d} usage={usage.data} canEdit={canEdit} />}
          {tab === "states" && <StatesTab slug={slug} def={d} usage={usage.data} canEdit={canEdit} />}
          {tab === "fields" && <FieldsTab slug={slug} def={d} usage={usage.data} canEdit={canEdit} />}
          {tab === "timeboxes" && <TimeboxesTab slug={slug} def={d} canEdit={canEdit} />}
          {tab === "history" && (canEdit ? <HistoryTab slug={slug} def={d} /> : <p className="muted">History is for this workflow's administrators.</p>)}
        </>
      )}
    </>
  );
}
